import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { HistoryEntryItem } from "./history-entry-item";
import { useStores } from "../../hooks/use-stores";
import { DocumentModelType } from "../../models/document/document";
import { TreeManagerType } from "../../models/history/tree-manager";
import { HistoryEntry, HistoryEntryType } from "../../models/history/history";
import {
  getHistoryPath, loadHistory, IFirestoreHistoryEntryDoc
} from "../../models/history/history-firestore";
import { FirestoreHistoryManagerConcurrent } from "../../models/history/firestore-history-manager-concurrent";

import "./history-view-panel.scss";

interface IHistoryViewPanelProps {
  document: DocumentModelType;
}

interface HistoryEntryWithMeta {
  index: number;
  previousEntryId?: string;
  entry: HistoryEntryType;
}

interface HistoryEntryWithMeta {
  index: number;
  previousEntryId?: string;
  entry: HistoryEntryType;
}

export const HistoryViewPanel: React.FC<IHistoryViewPanelProps> = observer(function HistoryViewPanel({
  document
}) {
  const stores = useStores();
  const { persistentUI, db } = stores;
  const [remoteHistoryEntries, setRemoteHistoryEntries] = useState<HistoryEntryWithMeta[]>([]);
  const [remoteHistoryError, setRemoteHistoryError] = useState<string | undefined>();

  // Get local history from document by casting to TreeManager instance
  const treeManager = document.treeManagerAPI as TreeManagerType | undefined;
  const localHistoryEntries = treeManager?.document.history || [];

  // Check if the history manager is a FirestoreHistoryManagerConcurrent (used for GroupDocuments)
  const historyManager = treeManager?.historyManager;
  const concurrentManager = historyManager instanceof FirestoreHistoryManagerConcurrent
    ? historyManager
    : undefined;

  // Get history path for remote history
  const historyPath = getHistoryPath(document.key);

  const showRemoteHistory = persistentUI.showRemoteHistoryView;

  const handleToggleRemoteHistory = () => {
    persistentUI.toggleRemoteHistoryView();
  };

  const handleClose = () => {
    persistentUI.setShowHistoryView(false);
  };

  // Subscribe to remote history when showRemoteHistory is enabled
  useEffect(() => {
    if (!showRemoteHistory || !historyPath) {
      setRemoteHistoryEntries([]);
      setRemoteHistoryError(undefined);
      return;
    }

    const unsubscribe = loadHistory(db.firestore, historyPath, (docs: IFirestoreHistoryEntryDoc[], error) => {
      if (error) {
        setRemoteHistoryError(error.message);
        setRemoteHistoryEntries([]);
      } else {
        setRemoteHistoryError(undefined);
        setRemoteHistoryEntries(docs.map(doc => {
          const { entry } = doc;
          return {
            index: doc.index,
            previousEntryId: doc.previousEntryId,
            entry: HistoryEntry.create(entry)
          };
        }));
      }
    });

    return () => unsubscribe();
  }, [showRemoteHistory, historyPath, db.firestore]);

  return (
    <div className="history-view-panel">
      <div className="history-view-header">
        <h3>Document History</h3>
        <button className="history-view-close" onClick={handleClose}>×</button>
      </div>

      {/* Local History Section */}
      <div className="history-view-section">
        <div className="history-view-section-header">
          <h4>Local History (Current Session)</h4>
          <span className="history-view-count">{localHistoryEntries.length} entries</span>
        </div>
        <div className="history-view-list">
          {localHistoryEntries.length === 0 ? (
            <div className="history-view-empty">No local history entries</div>
          ) : (
            localHistoryEntries.map((entry, index) => (
              <HistoryEntryItem key={entry.id} entry={entry} index={index} />
            ))
          )}
        </div>
      </div>

      {/* Remote History Toggle */}
      <div className="history-view-toggle">
        <label>
          <input
            type="checkbox"
            checked={showRemoteHistory}
            onChange={handleToggleRemoteHistory}
          />
          Show Remote History (Firestore)
        </label>
      </div>

      {/* Remote History Section */}
      {showRemoteHistory && (
        <div className="history-view-section">
          <div className="history-view-section-header">
            <h4>Remote History (Firestore)</h4>
            <span className="history-view-count">{remoteHistoryEntries.length} entries</span>
            {concurrentManager && (
              <div className="history-manager-controls">
                <button
                  className={concurrentManager.paused ? "paused" : ""}
                  onClick={() => concurrentManager.pauseUploads()}
                  disabled={concurrentManager.paused}
                >
                  {concurrentManager.paused ? "Paused" : "Pause Uploads"}
                </button>
                <button
                  onClick={() => concurrentManager.resumeUploadsAfterDelay(5000)}
                  disabled={!concurrentManager.paused}
                >
                  Resume After 5s
                </button>
              </div>
            )}
          </div>
          <div className="history-view-list">
            {remoteHistoryError ? (
              <div className="history-view-error">Error: {remoteHistoryError}</div>
            ) : remoteHistoryEntries.length === 0 ? (
              <div className="history-view-empty">No remote history entries</div>
            ) : (
              remoteHistoryEntries.map((entryWithMeta) => (
                <HistoryEntryItem
                  key={entryWithMeta.entry.id}
                  entry={entryWithMeta.entry}
                  index={entryWithMeta.index}
                  previousEntryId={entryWithMeta.previousEntryId}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
});
